import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const PROVIDERS = [
  {
    id: 'quickbooks',
    name: 'QuickBooks Online',
    logo: 'BookOpen',
    color: 'text-green-600 bg-green-50 border-green-200',
    description: 'Sync invoices, payments, and customers with QuickBooks Online',
    features: ['Invoice sync', 'Payment sync', 'Customer sync', 'Chart of accounts']
  },
  {
    id: 'xero',
    name: 'Xero',
    logo: 'Calculator',
    color: 'text-blue-600 bg-blue-50 border-blue-200',
    description: 'Connect with Xero for seamless accounting data synchronization',
    features: ['Invoice sync', 'Payment sync', 'Contact sync', 'Bank reconciliation']
  }
];

const AccountingSync = () => {
  const [integrations, setIntegrations] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [connectForm, setConnectForm] = useState({
    realm_id: '', tenant_id: '', company_name: '',
    sync_invoices: true, sync_payments: true, sync_customers: true, sync_products: false,
    auto_sync_enabled: false, sync_frequency: 'daily'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [intRes, logsRes, mapRes] = await Promise.all([
        supabase?.from('accounting_integrations')?.select('*')?.order('created_at'),
        supabase?.from('accounting_sync_logs')?.select('*, integration:accounting_integrations(provider, company_name)')?.order('started_at', { ascending: false })?.limit(50),
        supabase?.from('accounting_entity_mappings')?.select('*, integration:accounting_integrations(provider)')?.order('last_synced_at', { ascending: false })?.limit(100)
      ]);
      setIntegrations(intRes?.data || []);
      setSyncLogs(logsRes?.data || []);
      setMappings(mapRes?.data || []);
    } catch (err) {
      console.error('Error fetching accounting data:', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const showSuccess = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 4000); };
  const showError = (msg) => { setErrorMsg(msg); setTimeout(() => setErrorMsg(''), 5000); };

  const handleConnect = async () => {
    try {
      const existing = integrations?.find(i => i?.provider === selectedProvider?.id);
      const payload = {
        provider: selectedProvider?.id,
        status: 'connected',
        realm_id: connectForm?.realm_id || null,
        tenant_id: connectForm?.tenant_id || null,
        company_name: connectForm?.company_name,
        sync_invoices: connectForm?.sync_invoices,
        sync_payments: connectForm?.sync_payments,
        sync_customers: connectForm?.sync_customers,
        sync_products: connectForm?.sync_products,
        auto_sync_enabled: connectForm?.auto_sync_enabled,
        sync_frequency: connectForm?.sync_frequency,
        updated_at: new Date()?.toISOString()
      };
      if (existing?.id) {
        await supabase?.from('accounting_integrations')?.update(payload)?.eq('id', existing?.id);
      } else {
        await supabase?.from('accounting_integrations')?.insert([payload]);
      }
      setShowConnectModal(false);
      setConnectForm({ realm_id: '', tenant_id: '', company_name: '', sync_invoices: true, sync_payments: true, sync_customers: true, sync_products: false, auto_sync_enabled: false, sync_frequency: 'daily' });
      showSuccess(`${selectedProvider?.name} connected successfully`);
      fetchData();
    } catch (err) {
      showError('Failed to connect: ' + err?.message);
    }
  };

  const handleDisconnect = async (integration) => {
    if (!window.confirm(`Disconnect ${integration?.company_name || integration?.provider}?`)) return;
    await supabase?.from('accounting_integrations')?.update({ status: 'disconnected', updated_at: new Date()?.toISOString() })?.eq('id', integration?.id);
    showSuccess('Integration disconnected');
    fetchData();
  };

  const handleSync = async (integration, syncType = 'full') => {
    try {
      setSyncing(integration?.id);
      // Create sync log entry
      const { data: logEntry } = await supabase?.from('accounting_sync_logs')?.insert([{
        integration_id: integration?.id,
        sync_type: syncType,
        status: 'running',
        started_at: new Date()?.toISOString()
      }])?.select()?.single();

      // Simulate sync process
      const syncResults = { records_synced: 0, records_failed: 0, details: [] };

      if (integration?.sync_invoices) {
        const { data: invoices } = await supabase?.from('invoices')?.select('id, invoice_number, total_amount, invoice_status')?.limit(50);
        const invCount = invoices?.length || 0;
        syncResults.records_synced += invCount;
        syncResults?.details?.push({ type: 'invoices', count: invCount, status: 'success' });
        // Create mappings for invoices
        if (invoices?.length > 0) {
          const invMappings = invoices?.map(inv => ({
            integration_id: integration?.id,
            entity_type: 'invoice',
            local_id: inv?.id,
            external_id: `${integration?.provider?.toUpperCase()}-INV-${inv?.invoice_number}`,
            sync_status: 'synced',
            last_synced_at: new Date()?.toISOString()
          }));
          await supabase?.from('accounting_entity_mappings')?.upsert(invMappings, { onConflict: 'integration_id,local_id' });
        }
      }

      if (integration?.sync_customers) {
        const { data: customers } = await supabase?.from('customers')?.select('id, name, email')?.limit(50);
        const custCount = customers?.length || 0;
        syncResults.records_synced += custCount;
        syncResults?.details?.push({ type: 'customers', count: custCount, status: 'success' });
        if (customers?.length > 0) {
          const custMappings = customers?.map(c => ({
            integration_id: integration?.id,
            entity_type: 'customer',
            local_id: c?.id,
            external_id: `${integration?.provider?.toUpperCase()}-CUST-${c?.id?.slice(0, 8)}`,
            sync_status: 'synced',
            last_synced_at: new Date()?.toISOString()
          }));
          await supabase?.from('accounting_entity_mappings')?.upsert(custMappings, { onConflict: 'integration_id,local_id' });
        }
      }

      if (integration?.sync_payments) {
        const { data: payments } = await supabase?.from('payments')?.select('id, payment_number, amount')?.limit(50);
        const payCount = payments?.length || 0;
        syncResults.records_synced += payCount;
        syncResults?.details?.push({ type: 'payments', count: payCount, status: 'success' });
      }

      // Update log as completed
      await supabase?.from('accounting_sync_logs')?.update({
        status: 'completed',
        records_synced: syncResults?.records_synced,
        records_failed: syncResults?.records_failed,
        details: syncResults?.details,
        completed_at: new Date()?.toISOString()
      })?.eq('id', logEntry?.id);

      // Update integration last synced
      await supabase?.from('accounting_integrations')?.update({ last_synced_at: new Date()?.toISOString(), updated_at: new Date()?.toISOString() })?.eq('id', integration?.id);

      showSuccess(`Sync complete: ${syncResults?.records_synced} records synced`);
      fetchData();
    } catch (err) {
      showError('Sync failed: ' + err?.message);
    } finally {
      setSyncing(null);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await supabase?.from('accounting_integrations')?.update({
        sync_invoices: connectForm?.sync_invoices,
        sync_payments: connectForm?.sync_payments,
        sync_customers: connectForm?.sync_customers,
        sync_products: connectForm?.sync_products,
        auto_sync_enabled: connectForm?.auto_sync_enabled,
        sync_frequency: connectForm?.sync_frequency,
        updated_at: new Date()?.toISOString()
      })?.eq('id', editingIntegration?.id);
      setShowSettingsModal(false);
      showSuccess('Settings updated');
      fetchData();
    } catch (err) {
      showError('Failed to update settings: ' + err?.message);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      connected: 'bg-green-100 text-green-800',
      disconnected: 'bg-gray-100 text-gray-600',
      error: 'bg-red-100 text-red-800',
      syncing: 'bg-blue-100 text-blue-800'
    };
    return map?.[status] || 'bg-gray-100 text-gray-600';
  };

  const getLogStatusBadge = (status) => {
    const map = { completed: 'bg-green-100 text-green-800', running: 'bg-blue-100 text-blue-800', failed: 'bg-red-100 text-red-800', pending: 'bg-yellow-100 text-yellow-800' };
    return map?.[status] || 'bg-gray-100 text-gray-600';
  };

  const formatDate = (d) => d ? new Date(d)?.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never';

  const connectedProviders = integrations?.filter(i => i?.status === 'connected')?.map(i => i?.provider);

  const mappingsByType = mappings?.reduce((acc, m) => {
    acc[m?.entity_type] = (acc?.[m?.entity_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Accounting Sync | FieldServi</title></Helmet>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Accounting Software Sync</h1>
            <p className="text-muted-foreground mt-1">Connect QuickBooks or Xero to sync invoices, payments, and customer data</p>
          </div>
        </div>

        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <Icon name="CheckCircle" size={16} />{successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <Icon name="AlertCircle" size={16} />{errorMsg}
          </div>
        )}

        {/* Provider Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {PROVIDERS?.map(provider => {
            const integration = integrations?.find(i => i?.provider === provider?.id);
            const isConnected = integration?.status === 'connected';
            return (
              <div key={provider?.id} className={`bg-card border-2 rounded-xl p-5 ${isConnected ? 'border-green-200' : 'border-border'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl border ${provider?.color}`}>
                      <Icon name={provider?.logo} size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{provider?.name}</h3>
                      {isConnected && integration?.company_name && (
                        <p className="text-sm text-muted-foreground">{integration?.company_name}</p>
                      )}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${isConnected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {isConnected ? 'Connected' : 'Not Connected'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{provider?.description}</p>
                <div className="flex flex-wrap gap-1 mb-4">
                  {provider?.features?.map(f => (
                    <span key={f} className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">{f}</span>
                  ))}
                </div>
                {isConnected && (
                  <div className="text-xs text-muted-foreground mb-3">
                    Last synced: {formatDate(integration?.last_synced_at)}
                  </div>
                )}
                <div className="flex gap-2">
                  {isConnected ? (
                    <>
                      <Button size="sm" onClick={() => handleSync(integration)} disabled={syncing === integration?.id}>
                        {syncing === integration?.id ? (
                          <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>Syncing...</>
                        ) : (
                          <><Icon name="RefreshCw" size={12} className="mr-1" />Sync Now</>
                        )}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => {
                        setEditingIntegration(integration);
                        setConnectForm({ sync_invoices: integration?.sync_invoices, sync_payments: integration?.sync_payments, sync_customers: integration?.sync_customers, sync_products: integration?.sync_products, auto_sync_enabled: integration?.auto_sync_enabled, sync_frequency: integration?.sync_frequency || 'daily' });
                        setShowSettingsModal(true);
                      }}>
                        <Icon name="Settings" size={12} className="mr-1" />Settings
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDisconnect(integration)} className="text-red-600 hover:text-red-700">
                        <Icon name="Unlink" size={12} className="mr-1" />Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" onClick={() => {
                      setSelectedProvider(provider);
                      setConnectForm({ realm_id: '', tenant_id: '', company_name: '', sync_invoices: true, sync_payments: true, sync_customers: true, sync_products: false, auto_sync_enabled: false, sync_frequency: 'daily' });
                      setShowConnectModal(true);
                    }}>
                      <Icon name="Link" size={12} className="mr-1" />Connect
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mb-6">
          {[
            { id: 'overview', label: 'Sync Overview', icon: 'BarChart3' },
            { id: 'logs', label: 'Sync Logs', icon: 'Activity' },
            { id: 'mappings', label: 'Entity Mappings', icon: 'GitMerge' }
          ]?.map(tab => (
            <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              <Icon name={tab?.icon} size={16} />{tab?.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
        ) : activeTab === 'overview' ? (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Invoices Synced', value: mappingsByType?.invoice || 0, icon: 'FileText', color: 'text-blue-600 bg-blue-50' },
                { label: 'Customers Synced', value: mappingsByType?.customer || 0, icon: 'Users', color: 'text-green-600 bg-green-50' },
                { label: 'Payments Synced', value: mappingsByType?.payment || 0, icon: 'DollarSign', color: 'text-purple-600 bg-purple-50' },
                { label: 'Total Syncs', value: syncLogs?.filter(l => l?.status === 'completed')?.length, icon: 'RefreshCw', color: 'text-orange-600 bg-orange-50' }
              ]?.map(stat => (
                <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${stat?.color}`}><Icon name={stat?.icon} size={18} /></div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                      <p className="text-xs text-muted-foreground">{stat?.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {connectedProviders?.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground bg-card border border-border rounded-xl">
                <Icon name="Link" size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No accounting software connected</p>
                <p className="text-sm mt-1">Connect QuickBooks or Xero above to start syncing your data</p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-foreground mb-4">Sync Configuration</h3>
                <div className="space-y-3">
                  {integrations?.filter(i => i?.status === 'connected')?.map(integration => (
                    <div key={integration?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium text-foreground capitalize">{integration?.provider} — {integration?.company_name}</p>
                        <div className="flex gap-3 mt-1">
                          {[['sync_invoices', 'Invoices'], ['sync_payments', 'Payments'], ['sync_customers', 'Customers'], ['sync_products', 'Products']]?.map(([key, label]) => (
                            <span key={key} className={`text-xs ${integration?.[key] ? 'text-green-600' : 'text-gray-400'}`}>
                              <Icon name={integration?.[key] ? 'CheckCircle' : 'Circle'} size={10} className="inline mr-0.5" />{label}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Auto-sync: {integration?.auto_sync_enabled ? `${integration?.sync_frequency}` : 'Off'}</p>
                        <p className="text-xs text-muted-foreground">Last: {formatDate(integration?.last_synced_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'logs' ? (
          <div>
            {syncLogs?.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Icon name="Activity" size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No sync logs yet</p>
                <p className="text-sm mt-1">Sync logs will appear here after running a sync</p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {['Provider', 'Type', 'Status', 'Records', 'Started', 'Duration']?.map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {syncLogs?.map(log => {
                      const duration = log?.completed_at && log?.started_at
                        ? Math.round((new Date(log?.completed_at) - new Date(log?.started_at)) / 1000)
                        : null;
                      return (
                        <tr key={log?.id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3">
                            <div className="font-medium text-foreground capitalize">{log?.integration?.provider || 'N/A'}</div>
                            <div className="text-xs text-muted-foreground">{log?.integration?.company_name}</div>
                          </td>
                          <td className="px-4 py-3 capitalize text-foreground">{log?.sync_type}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getLogStatusBadge(log?.status)}`}>{log?.status}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-green-600">{log?.records_synced} synced</span>
                            {log?.records_failed > 0 && <span className="text-red-500 ml-2">{log?.records_failed} failed</span>}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{formatDate(log?.started_at)}</td>
                          <td className="px-4 py-3 text-muted-foreground">{duration !== null ? `${duration}s` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div>
            {mappings?.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Icon name="GitMerge" size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No entity mappings yet</p>
                <p className="text-sm mt-1">Run a sync to create entity mappings between systems</p>
              </div>
            ) : (
              <div>
                <div className="flex gap-2 mb-4 flex-wrap">
                  {['invoice', 'customer', 'payment', 'product']?.map(type => {
                    const count = mappingsByType?.[type] || 0;
                    return count > 0 ? (
                      <span key={type} className="px-3 py-1.5 bg-muted text-muted-foreground text-xs rounded-full capitalize">
                        {type}s: {count}
                      </span>
                    ) : null;
                  })}
                </div>
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        {['Provider', 'Type', 'Local ID', 'External ID', 'Status', 'Last Synced']?.map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {mappings?.map(mapping => (
                        <tr key={mapping?.id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 capitalize text-foreground">{mapping?.integration?.provider}</td>
                          <td className="px-4 py-3 capitalize text-foreground">{mapping?.entity_type}</td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{mapping?.local_id?.slice(0, 8)}...</td>
                          <td className="px-4 py-3 font-mono text-xs text-foreground">{mapping?.external_id}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${mapping?.sync_status === 'synced' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {mapping?.sync_status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{formatDate(mapping?.last_synced_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Connect Modal */}
      {showConnectModal && selectedProvider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg border ${selectedProvider?.color}`}><Icon name={selectedProvider?.logo} size={20} /></div>
                <h2 className="text-lg font-semibold text-foreground">Connect {selectedProvider?.name}</h2>
              </div>
              <button onClick={() => setShowConnectModal(false)} className="text-muted-foreground hover:text-foreground"><Icon name="X" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                <Icon name="Info" size={14} className="inline mr-1" />
                In production, this would redirect to {selectedProvider?.name}'s OAuth flow. Enter your connection details below.
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Company Name *</label>
                <input value={connectForm?.company_name} onChange={e => setConnectForm(p => ({ ...p, company_name: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder={`Your ${selectedProvider?.name} company name`} />
              </div>
              {selectedProvider?.id === 'quickbooks' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Realm ID</label>
                  <input value={connectForm?.realm_id} onChange={e => setConnectForm(p => ({ ...p, realm_id: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder="QuickBooks Realm ID" />
                </div>
              )}
              {selectedProvider?.id === 'xero' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Tenant ID</label>
                  <input value={connectForm?.tenant_id} onChange={e => setConnectForm(p => ({ ...p, tenant_id: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder="Xero Tenant ID" />
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Sync Settings</p>
                <div className="space-y-2">
                  {[['sync_invoices', 'Sync Invoices'], ['sync_payments', 'Sync Payments'], ['sync_customers', 'Sync Customers'], ['sync_products', 'Sync Products']]?.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={connectForm?.[key]} onChange={e => setConnectForm(p => ({ ...p, [key]: e?.target?.checked }))} className="rounded" />
                      <span className="text-sm text-foreground">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={connectForm?.auto_sync_enabled} onChange={e => setConnectForm(p => ({ ...p, auto_sync_enabled: e?.target?.checked }))} className="rounded" />
                    <span className="text-sm text-foreground">Auto-sync</span>
                  </label>
                </div>
                {connectForm?.auto_sync_enabled && (
                  <div>
                    <select value={connectForm?.sync_frequency} onChange={e => setConnectForm(p => ({ ...p, sync_frequency: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground">
                      {['hourly', 'daily', 'weekly']?.map(f => <option key={f} value={f} className="capitalize">{f}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-border">
              <Button variant="outline" onClick={() => setShowConnectModal(false)}>Cancel</Button>
              <Button onClick={handleConnect} disabled={!connectForm?.company_name}>Connect</Button>
            </div>
          </div>
        </div>
      )}
      {/* Settings Modal */}
      {showSettingsModal && editingIntegration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">Sync Settings — {editingIntegration?.company_name}</h2>
              <button onClick={() => setShowSettingsModal(false)} className="text-muted-foreground hover:text-foreground"><Icon name="X" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Data to Sync</p>
                <div className="space-y-2">
                  {[['sync_invoices', 'Sync Invoices'], ['sync_payments', 'Sync Payments'], ['sync_customers', 'Sync Customers'], ['sync_products', 'Sync Products']]?.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={connectForm?.[key]} onChange={e => setConnectForm(p => ({ ...p, [key]: e?.target?.checked }))} className="rounded" />
                      <span className="text-sm text-foreground">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input type="checkbox" checked={connectForm?.auto_sync_enabled} onChange={e => setConnectForm(p => ({ ...p, auto_sync_enabled: e?.target?.checked }))} className="rounded" />
                  <span className="text-sm text-foreground">Enable Auto-sync</span>
                </label>
                {connectForm?.auto_sync_enabled && (
                  <select value={connectForm?.sync_frequency} onChange={e => setConnectForm(p => ({ ...p, sync_frequency: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground">
                    {['hourly', 'daily', 'weekly']?.map(f => <option key={f} value={f} className="capitalize">{f}</option>)}
                  </select>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-border">
              <Button variant="outline" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
              <Button onClick={handleUpdateSettings}>Save Settings</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingSync;
